'use client';
import React, { useState, useEffect, useRef } from 'react';
import dynamic from 'next/dynamic';
import { QROptions, QRType } from './QRGeneratorClient';

const QRCodeCanvas = dynamic(() => import('qrcode.react').then(m => m.QRCodeCanvas), { ssr: false });

const typeLabels: Record<QRType, string> = {
  url: 'Website URL',
  text: 'Plain Text',
  whatsapp: 'WhatsApp',
  phone: 'Phone Number',
  email: 'Email',
  maps: 'Google Maps',
  wifi: 'WiFi Network',
  upi: 'UPI Payment',
};

const typeBadgeClass: Record<QRType, string> = {
  url: 'badge-blue',
  text: 'badge-slate',
  whatsapp: 'badge-green',
  phone: 'badge-cyan',
  email: 'badge-violet',
  maps: 'badge-rose',
  wifi: 'badge-amber',
  upi: 'badge-orange',
};

interface QRPreviewPanelProps {
  qrValue: string;
  isGenerated: boolean;
  options: QROptions;
  activeType: QRType;
  canvasRef: React.RefObject<HTMLCanvasElement>;
}

// Dots-style QR renderer using canvas
function DotsQRCanvas({
  value,
  size,
  fgColor,
  bgColor,
  transparentBg,
  errorLevel,
  logoUrl,
  logoSize,
}: {
  value: string;
  size: number;
  fgColor: string;
  bgColor: string;
  transparentBg: boolean;
  errorLevel: 'L' | 'M' | 'Q' | 'H';
  logoUrl: string | null;
  logoSize: number;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    if (!value || !canvasRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    import('qrcode').then(async QRCode => {
      // Get QR matrix data
      const qrData = QRCode.create(value, { errorCorrectionLevel: errorLevel });
      const modules = qrData.modules;
      const moduleCount = modules.size;

      const quietZoneModules = 2;
      const moduleSize = size / (moduleCount + quietZoneModules * 2);
      const marginPx = quietZoneModules * moduleSize;

      canvas.width = size;
      canvas.height = size;

      // Background
      if (transparentBg) {
        ctx.clearRect(0, 0, size, size);
      } else {
        ctx.fillStyle = bgColor;
        ctx.fillRect(0, 0, size, size);
      }

      ctx.fillStyle = fgColor;

      for (let row = 0; row < moduleCount; row++) {
        for (let col = 0; col < moduleCount; col++) {
          if (modules.get(row, col)) {
            const x = marginPx + col * moduleSize;
            const y = marginPx + row * moduleSize;
            const r = moduleSize / 2;
            ctx.beginPath();
            ctx.arc(x + r, y + r, r * 0.85, 0, Math.PI * 2);
            ctx.fill();
          }
        }
      }

      // Overlay logo if present
      if (logoUrl) {
        await new Promise<void>((resolve) => {
          const img = new Image();
          img.crossOrigin = 'anonymous';
          img.onload = () => {
            const ls = Math.round(size * (logoSize / 100));
            const lx = Math.round((size - ls) / 2);
            const ly = Math.round((size - ls) / 2);
            const padding = Math.round(ls * 0.15);
            ctx.fillStyle = transparentBg ? '#ffffff' : bgColor;
            ctx.beginPath();
            const rx = lx - padding;
            const ry = ly - padding;
            const rw = ls + padding * 2;
            const rh = ls + padding * 2;
            const rr = Math.round(padding * 1.2);
            ctx.moveTo(rx + rr, ry);
            ctx.lineTo(rx + rw - rr, ry);
            ctx.quadraticCurveTo(rx + rw, ry, rx + rw, ry + rr);
            ctx.lineTo(rx + rw, ry + rh - rr);
            ctx.quadraticCurveTo(rx + rw, ry + rh, rx + rw - rr, ry + rh);
            ctx.lineTo(rx + rr, ry + rh);
            ctx.quadraticCurveTo(rx, ry + rh, rx, ry + rh - rr);
            ctx.lineTo(rx, ry + rr);
            ctx.quadraticCurveTo(rx, ry, rx + rr, ry);
            ctx.closePath();
            ctx.fill();
            ctx.drawImage(img, lx, ly, ls, ls);
            resolve();
          };
          img.onerror = () => resolve();
          img.src = logoUrl;
        });
      }
    });
  }, [value, size, fgColor, bgColor, transparentBg, errorLevel, logoUrl, logoSize]);

  return (
    <canvas
      ref={canvasRef}
      width={size}
      height={size}
      style={{ display: 'block', maxWidth: '100%', borderRadius: '4px' }}
    />
  );
}

export default function QRPreviewPanel({ qrValue, isGenerated, options, activeType }: QRPreviewPanelProps) {
  const [qrSize] = useState(260);
  const ecLevel = options.errorLevel as 'L' | 'M' | 'Q' | 'H';

  return (
    <div className="bg-card rounded-3xl border border-border shadow-elevated overflow-hidden">
      {/* Header */}
      <div className="px-5 py-4 border-b border-border flex items-center justify-between">
        <h2 className="font-semibold text-foreground" style={{ fontSize: '15px' }}>Preview</h2>
        {isGenerated && (
          <span className={`badge ${typeBadgeClass[activeType]}`}>{typeLabels[activeType]}</span>
        )}
      </div>

      {/* QR Preview area */}
      <div className="qr-preview-container p-6 sm:p-8 flex flex-col items-center justify-center" style={{ minHeight: '280px' }}>
        {isGenerated && qrValue ? (
          <div className="animate-scale-in flex flex-col items-center gap-4 w-full">
            {/* QR Code — centered, large on mobile */}
            <div
              className="rounded-2xl p-4 shadow-xl-custom mx-auto"
              style={{
                background: options.transparentBg ? 'transparent' : options.bgColor,
                display: 'inline-block',
              }}
            >
              {options.style === 'dots' ? (
                <DotsQRCanvas
                  value={qrValue}
                  size={qrSize}
                  fgColor={options.fgColor}
                  bgColor={options.bgColor}
                  transparentBg={options.transparentBg}
                  errorLevel={ecLevel}
                  logoUrl={options.logoUrl}
                  logoSize={options.logoSize}
                />
              ) : (
                <QRCodeCanvas
                  value={qrValue}
                  size={qrSize}
                  fgColor={options.fgColor}
                  bgColor={options.transparentBg ? 'transparent' : options.bgColor}
                  level={ecLevel}
                  imageSettings={
                    options.logoUrl
                      ? {
                          src: options.logoUrl,
                          height: Math.round(qrSize * (options.logoSize / 100)),
                          width: Math.round(qrSize * (options.logoSize / 100)),
                          excavate: true,
                        }
                      : undefined
                  }
                  style={{
                    borderRadius: options.style === 'rounded' ? '8px' : '0px',
                    display: 'block',
                    maxWidth: '100%',
                  }}
                />
              )}
            </div>
            <div className="text-center px-2">
              <p className="text-muted-foreground" style={{ fontSize: '12px', maxWidth: '260px', wordBreak: 'break-all', margin: '0 auto' }}>
                {qrValue.length > 60 ? qrValue.slice(0, 60) + '...' : qrValue}
              </p>
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-4 text-center py-6">
            <div className="w-20 h-20 rounded-2xl gradient-primary-soft border border-border flex items-center justify-center">
              <svg width="36" height="36" viewBox="0 0 36 36" fill="none" opacity="0.4">
                <rect x="3" y="3" width="11" height="11" rx="2.5" stroke="var(--primary)" strokeWidth="2" />
                <rect x="22" y="3" width="11" height="11" rx="2.5" stroke="var(--primary)" strokeWidth="2" />
                <rect x="3" y="22" width="11" height="11" rx="2.5" stroke="var(--primary)" strokeWidth="2" />
                <rect x="6.5" y="6.5" width="4" height="4" rx="1" fill="var(--primary)" />
                <rect x="25.5" y="6.5" width="4" height="4" rx="1" fill="var(--primary)" />
                <rect x="6.5" y="25.5" width="4" height="4" rx="1" fill="var(--primary)" />
                <rect x="22" y="22" width="4" height="4" rx="1" fill="var(--primary)" />
                <rect x="29" y="22" width="4" height="4" rx="1" fill="var(--primary)" />
                <rect x="22" y="29" width="4" height="4" rx="1" fill="var(--primary)" />
                <rect x="29" y="29" width="4" height="4" rx="1" fill="var(--primary)" />
              </svg>
            </div>
            <div>
              <p className="font-semibold text-foreground" style={{ fontSize: '15px' }}>No QR Code yet</p>
              <p className="text-muted-foreground mt-1" style={{ fontSize: '13px', maxWidth: '220px', margin: '4px auto 0' }}>
                Fill in the content fields and click Generate to create your QR code.
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Size indicator */}
      {isGenerated && (
        <div className="px-5 py-3 border-t border-border flex items-center justify-between">
          <span className="text-muted-foreground" style={{ fontSize: '12px' }}>Output: {options.size}px × {options.size}px</span>
          <span className="text-muted-foreground" style={{ fontSize: '12px' }}>EC: {options.errorLevel}</span>
        </div>
      )}
    </div>
  );
}